import React, { useState } from 'react';
import { ShieldIcon, KeyIcon, UsersIcon, LockIcon, NetworkIcon, ClockIcon, EyeIcon, DatabaseIcon, ListIcon, AlertCircleIcon, CheckCircleIcon, XCircleIcon, UserPlusIcon, ShieldCheckIcon, TimerIcon, RefreshCwIcon, ServerIcon, HashIcon } from 'lucide-react';
export const SecuritySettings = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [mfaEnabled, setMfaEnabled] = useState(true);
  const [selectedRole, setSelectedRole] = useState<string | null>(null);
  return <div className="space-y-8" data-id="element-1380">
      {/* Role & Permission Management */}
      <div className="border-b border-gray-200 pb-8" data-id="element-1381">
        <h3 className="text-base font-semibold text-gray-900 mb-6" data-id="element-1382">
          Role & Permission Management
        </h3>
        <div className="space-y-6" data-id="element-1383">
          {/* Role Configuration */}
          <div data-id="element-1384">
            <label className="block text-sm font-medium text-gray-700 mb-4" data-id="element-1385">
              System Roles
            </label>
            <div className="space-y-4" data-id="element-1386">
              {[{
              name: 'System Administrator',
              description: 'Full system access and configuration rights',
              permissions: ['all'],
              users: 3
            }, {
              name: 'Financial Officer',
              description: 'Access to financial operations and reports',
              permissions: ['finance.view', 'finance.edit', 'reports.view'],
              users: 8
            }, {
              name: 'Service Desk',
              description: 'Customer service and basic operations',
              permissions: ['service.view', 'service.edit', 'customer.view'],
              users: 15
            }].map(role => <div key={role.name} className="border border-gray-200 rounded-lg p-4" data-id="element-1387">
                  <div className="flex items-center justify-between mb-3" data-id="element-1388">
                    <div data-id="element-1389">
                      <h4 className="text-sm font-medium text-gray-900" data-id="element-1390">
                        {role.name}
                      </h4>
                      <p className="text-xs text-gray-500" data-id="element-1391">
                        {role.description}
                      </p>
                    </div>
                    <span className="text-xs text-gray-500" data-id="element-1392">
                      {role.users} users
                    </span>
                  </div>
                  <div className="space-y-3" data-id="element-1393">
                    <div className="flex flex-wrap gap-2" data-id="element-1394">
                      {role.permissions.map(permission => <span key={permission} className="px-2 py-1 text-xs bg-blue-100 text-blue-600 rounded" data-id="element-1395">
                          {permission}
                        </span>)}
                    </div>
                    <div className="flex justify-end space-x-2" data-id="element-1396">
                      <button className="text-sm text-gray-600 hover:text-gray-900" data-id="element-1397">
                        Edit
                      </button>
                      <button className="text-sm text-blue-600 hover:text-blue-700" data-id="element-1398">
                        Manage Users
                      </button>
                    </div>
                  </div>
                </div>)}
              <button className="w-full flex items-center justify-center px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50" data-id="element-1399">
                <UserPlusIcon className="h-4 w-4 mr-2" data-id="element-1400" />
                Create New Role
              </button>
            </div>
          </div>
          {/* Permission Matrix */}
          <div data-id="element-1401">
            <label className="block text-sm font-medium text-gray-700 mb-4" data-id="element-1402">
              Permission Matrix
            </label>
            <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-1403">
              <table className="min-w-full divide-y divide-gray-200" data-id="element-1404">
                <thead className="bg-gray-50" data-id="element-1405">
                  <tr data-id="element-1406">
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase" data-id="element-1407">
                      Permission
                    </th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase" data-id="element-1408">
                      View
                    </th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase" data-id="element-1409">
                      Create
                    </th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase" data-id="element-1410">
                      Edit
                    </th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase" data-id="element-1411">
                      Delete
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200" data-id="element-1412">
                  {['User Management', 'Financial Operations', 'Service Requests', 'Reports', 'System Settings'].map(module => <tr key={module} data-id="element-1413">
                      <td className="px-6 py-4 text-sm font-medium text-gray-900" data-id="element-1414">
                        {module}
                      </td>
                      <td className="px-6 py-4 text-center" data-id="element-1415">
                        <input type="checkbox" className="h-4 w-4 text-blue-600 rounded border-gray-300" defaultChecked data-id="element-1416" />
                      </td>
                      <td className="px-6 py-4 text-center" data-id="element-1417">
                        <input type="checkbox" className="h-4 w-4 text-blue-600 rounded border-gray-300" defaultChecked data-id="element-1418" />
                      </td>
                      <td className="px-6 py-4 text-center" data-id="element-1419">
                        <input type="checkbox" className="h-4 w-4 text-blue-600 rounded border-gray-300" defaultChecked data-id="element-1420" />
                      </td>
                      <td className="px-6 py-4 text-center" data-id="element-1421">
                        <input type="checkbox" className="h-4 w-4 text-blue-600 rounded border-gray-300" data-id="element-1422" />
                      </td>
                    </tr>)}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
      {/* Authentication & Password Policies */}
      <div className="border-b border-gray-200 pb-8" data-id="element-1423">
        <h3 className="text-base font-semibold text-gray-900 mb-6" data-id="element-1424">
          Authentication & Password Policies
        </h3>
        <div className="space-y-6" data-id="element-1425">
          {/* Password Requirements */}
          <div data-id="element-1426">
            <label className="block text-sm font-medium text-gray-700 mb-4" data-id="element-1427">
              Password Policy Configuration
            </label>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-1428">
              <div className="border border-gray-200 rounded-lg p-4" data-id="element-1429">
                <h4 className="text-sm font-medium text-gray-900 mb-4" data-id="element-1430">
                  Complexity Requirements
                </h4>
                <div className="space-y-2" data-id="element-1431">
                  <div className="flex items-center justify-between" data-id="element-1432">
                    <span className="text-sm text-gray-600" data-id="element-1433">
                      Minimum Length
                    </span>
                    <select className="text-sm border-gray-300 rounded-md" data-id="element-1434">
                      <option data-id="element-1435">8 characters</option>
                      <option data-id="element-1436">10 characters</option>
                      <option data-id="element-1437">12 characters</option>
                    </select>
                  </div>
                  <div className="flex items-center justify-between" data-id="element-1438">
                    <span className="text-sm text-gray-600" data-id="element-1439">
                      Require Numbers
                    </span>
                    <input type="checkbox" className="h-4 w-4 text-blue-600 rounded border-gray-300" defaultChecked data-id="element-1440" />
                  </div>
                  <div className="flex items-center justify-between" data-id="element-1441">
                    <span className="text-sm text-gray-600" data-id="element-1442">
                      Require Special Characters
                    </span>
                    <input type="checkbox" className="h-4 w-4 text-blue-600 rounded border-gray-300" defaultChecked data-id="element-1443" />
                  </div>
                  <div className="flex items-center justify-between" data-id="element-1444">
                    <span className="text-sm text-gray-600" data-id="element-1445">
                      Mixed Case Required
                    </span>
                    <input type="checkbox" className="h-4 w-4 text-blue-600 rounded border-gray-300" defaultChecked data-id="element-1446" />
                  </div>
                </div>
              </div>
              <div className="border border-gray-200 rounded-lg p-4" data-id="element-1447">
                <h4 className="text-sm font-medium text-gray-900 mb-4" data-id="element-1448">
                  Password History & Expiry
                </h4>
                <div className="space-y-2" data-id="element-1449">
                  <div className="flex items-center justify-between" data-id="element-1450">
                    <span className="text-sm text-gray-600" data-id="element-1451">
                      Password History
                    </span>
                    <select className="text-sm border-gray-300 rounded-md" data-id="element-1452">
                      <option data-id="element-1453">Last 5 passwords</option>
                      <option data-id="element-1454">Last 10 passwords</option>
                      <option data-id="element-1455">Last 15 passwords</option>
                    </select>
                  </div>
                  <div className="flex items-center justify-between" data-id="element-1456">
                    <span className="text-sm text-gray-600" data-id="element-1457">Expiry Period</span>
                    <select className="text-sm border-gray-300 rounded-md" data-id="element-1458">
                      <option data-id="element-1459">90 days</option>
                      <option data-id="element-1460">60 days</option>
                      <option data-id="element-1461">30 days</option>
                    </select>
                  </div>
                  <div className="flex items-center justify-between" data-id="element-1462">
                    <span className="text-sm text-gray-600" data-id="element-1463">Grace Period</span>
                    <select className="text-sm border-gray-300 rounded-md" data-id="element-1464">
                      <option data-id="element-1465">7 days</option>
                      <option data-id="element-1466">5 days</option>
                      <option data-id="element-1467">3 days</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>
          </div>
          {/* Account Lockout */}
          <div data-id="element-1468">
            <label className="block text-sm font-medium text-gray-700 mb-4" data-id="element-1469">
              Account Lockout Policy
            </label>
            <div className="border border-gray-200 rounded-lg p-4" data-id="element-1470">
              <div className="space-y-4" data-id="element-1471">
                <div className="flex items-center justify-between" data-id="element-1472">
                  <span className="text-sm text-gray-600" data-id="element-1473">
                    Maximum Failed Attempts
                  </span>
                  <select className="text-sm border-gray-300 rounded-md" data-id="element-1474">
                    <option data-id="element-1475">3 attempts</option>
                    <option data-id="element-1476">5 attempts</option>
                    <option data-id="element-1477">10 attempts</option>
                  </select>
                </div>
                <div className="flex items-center justify-between" data-id="element-1478">
                  <span className="text-sm text-gray-600" data-id="element-1479">
                    Lockout Duration
                  </span>
                  <select className="text-sm border-gray-300 rounded-md" data-id="element-1480">
                    <option data-id="element-1481">15 minutes</option>
                    <option data-id="element-1482">30 minutes</option>
                    <option data-id="element-1483">1 hour</option>
                  </select>
                </div>
                <div className="flex items-center justify-between" data-id="element-1484">
                  <span className="text-sm text-gray-600" data-id="element-1485">
                    Reset Counter After
                  </span>
                  <select className="text-sm border-gray-300 rounded-md" data-id="element-1486">
                    <option data-id="element-1487">24 hours</option>
                    <option data-id="element-1488">12 hours</option>
                    <option data-id="element-1489">6 hours</option>
                  </select>
                </div>
              </div>
            </div>
          </div>
          {/* MFA Settings */}
          <div data-id="element-1490">
            <label className="block text-sm font-medium text-gray-700 mb-4" data-id="element-1491">
              Multi-Factor Authentication
            </label>
            <div className="border border-gray-200 rounded-lg p-4" data-id="element-1492">
              <div className="flex items-center justify-between mb-4" data-id="element-1493">
                <div data-id="element-1494">
                  <h4 className="text-sm font-medium text-gray-900" data-id="element-1495">
                    Enable MFA
                  </h4>
                  <p className="text-xs text-gray-500" data-id="element-1496">
                    Require two-factor authentication for all users
                  </p>
                </div>
                <div className="relative inline-block w-12 mr-2 align-middle select-none" data-id="element-1497">
                  <input type="checkbox" checked={mfaEnabled} onChange={e => setMfaEnabled(e.target.checked)} className="toggle-checkbox absolute block w-6 h-6 rounded-full bg-white border-4 appearance-none cursor-pointer" data-id="element-1498" />
                  <label className="toggle-label block overflow-hidden h-6 rounded-full bg-gray-300 cursor-pointer" data-id="element-1499"></label>
                </div>
              </div>
              <div className="space-y-3" data-id="element-1500">
                <div className="flex items-center" data-id="element-1501">
                  <input type="checkbox" className="h-4 w-4 text-blue-600 rounded border-gray-300" defaultChecked data-id="element-1502" />
                  <label className="ml-2 text-sm text-gray-600" data-id="element-1503">
                    Authenticator App
                  </label>
                </div>
                <div className="flex items-center" data-id="element-1504">
                  <input type="checkbox" className="h-4 w-4 text-blue-600 rounded border-gray-300" defaultChecked data-id="element-1505" />
                  <label className="ml-2 text-sm text-gray-600" data-id="element-1506">
                    SMS Authentication
                  </label>
                </div>
                <div className="flex items-center" data-id="element-1507">
                  <input type="checkbox" className="h-4 w-4 text-blue-600 rounded border-gray-300" data-id="element-1508" />
                  <label className="ml-2 text-sm text-gray-600" data-id="element-1509">
                    Email Authentication
                  </label>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Network & Session Security */}
      <div className="border-b border-gray-200 pb-8" data-id="element-1510">
        <h3 className="text-base font-semibold text-gray-900 mb-6" data-id="element-1511">
          Network & Session Security
        </h3>
        <div className="space-y-6" data-id="element-1512">
          {/* Session Management */}
          <div data-id="element-1513">
            <label className="block text-sm font-medium text-gray-700 mb-4" data-id="element-1514">
              Session Configuration
            </label>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-1515">
              <div className="border border-gray-200 rounded-lg p-4" data-id="element-1516">
                <h4 className="text-sm font-medium text-gray-900 mb-4" data-id="element-1517">
                  Session Timeouts
                </h4>
                <div className="space-y-3" data-id="element-1518">
                  <div className="flex items-center justify-between" data-id="element-1519">
                    <span className="text-sm text-gray-600" data-id="element-1520">Idle Timeout</span>
                    <select className="text-sm border-gray-300 rounded-md" data-id="element-1521">
                      <option data-id="element-1522">15 minutes</option>
                      <option data-id="element-1523">30 minutes</option>
                      <option data-id="element-1524">1 hour</option>
                    </select>
                  </div>
                  <div className="flex items-center justify-between" data-id="element-1525">
                    <span className="text-sm text-gray-600" data-id="element-1526">
                      Absolute Timeout
                    </span>
                    <select className="text-sm border-gray-300 rounded-md" data-id="element-1527">
                      <option data-id="element-1528">8 hours</option>
                      <option data-id="element-1529">12 hours</option>
                      <option data-id="element-1530">24 hours</option>
                    </select>
                  </div>
                </div>
              </div>
              <div className="border border-gray-200 rounded-lg p-4" data-id="element-1531">
                <h4 className="text-sm font-medium text-gray-900 mb-4" data-id="element-1532">
                  Session Security
                </h4>
                <div className="space-y-3" data-id="element-1533">
                  <div className="flex items-center" data-id="element-1534">
                    <input type="checkbox" className="h-4 w-4 text-blue-600 rounded border-gray-300" defaultChecked data-id="element-1535" />
                    <label className="ml-2 text-sm text-gray-600" data-id="element-1536">
                      Enforce Single Session
                    </label>
                  </div>
                  <div className="flex items-center" data-id="element-1537">
                    <input type="checkbox" className="h-4 w-4 text-blue-600 rounded border-gray-300" defaultChecked data-id="element-1538" />
                    <label className="ml-2 text-sm text-gray-600" data-id="element-1539">
                      Regenerate Session ID
                    </label>
                  </div>
                </div>
              </div>
            </div>
          </div>
          {/* IP Restrictions */}
          <div data-id="element-1540">
            <label className="block text-sm font-medium text-gray-700 mb-4" data-id="element-1541">
              IP Address Restrictions
            </label>
            <div className="border border-gray-200 rounded-lg p-4" data-id="element-1542">
              <div className="space-y-4" data-id="element-1543">
                <div data-id="element-1544">
                  <h4 className="text-sm font-medium text-gray-900 mb-2" data-id="element-1545">
                    Allowed IP Ranges
                  </h4>
                  <div className="space-y-2" data-id="element-1546">
                    {['10.0.0.0/8', '172.16.0.0/12', '192.168.0.0/16'].map(range => <div key={range} className="flex items-center justify-between" data-id="element-1547">
                          <span className="text-sm text-gray-600" data-id="element-1548">{range}</span>
                          <button className="text-sm text-red-600 hover:text-red-700" data-id="element-1549">
                            Remove
                          </button>
                        </div>)}
                    <button className="mt-2 text-sm text-blue-600 hover:text-blue-700" data-id="element-1550">
                      + Add IP Range
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
          {/* TLS Configuration */}
          <div data-id="element-1551">
            <label className="block text-sm font-medium text-gray-700 mb-4" data-id="element-1552">
              TLS Configuration
            </label>
            <div className="border border-gray-200 rounded-lg p-4" data-id="element-1553">
              <div className="space-y-4" data-id="element-1554">
                <div className="flex items-center justify-between" data-id="element-1555">
                  <span className="text-sm text-gray-600" data-id="element-1556">
                    Minimum TLS Version
                  </span>
                  <select className="text-sm border-gray-300 rounded-md" data-id="element-1557">
                    <option data-id="element-1558">TLS 1.2</option>
                    <option data-id="element-1559">TLS 1.3</option>
                  </select>
                </div>
                <div className="flex items-center" data-id="element-1560">
                  <input type="checkbox" className="h-4 w-4 text-blue-600 rounded border-gray-300" defaultChecked data-id="element-1561" />
                  <label className="ml-2 text-sm text-gray-600" data-id="element-1562">
                    Enforce HSTS
                  </label>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Data Protection & Privacy */}
      <div className="border-b border-gray-200 pb-8" data-id="element-1563">
        <h3 className="text-base font-semibold text-gray-900 mb-6" data-id="element-1564">
          Data Protection & Privacy
        </h3>
        <div className="space-y-6" data-id="element-1565">
          {/* Data Encryption */}
          <div data-id="element-1566">
            <label className="block text-sm font-medium text-gray-700 mb-4" data-id="element-1567">
              Data Encryption Policy
            </label>
            <div className="border border-gray-200 rounded-lg p-4" data-id="element-1568">
              <div className="space-y-4" data-id="element-1569">
                <div className="flex items-center justify-between" data-id="element-1570">
                  <span className="text-sm text-gray-600" data-id="element-1571">
                    Encryption Algorithm
                  </span>
                  <select className="text-sm border-gray-300 rounded-md" data-id="element-1572">
                    <option data-id="element-1573">AES-256-GCM</option>
                    <option data-id="element-1574">ChaCha20-Poly1305</option>
                  </select>
                </div>
                <div className="flex items-center" data-id="element-1575">
                  <input type="checkbox" className="h-4 w-4 text-blue-600 rounded border-gray-300" defaultChecked data-id="element-1576" />
                  <label className="ml-2 text-sm text-gray-600" data-id="element-1577">
                    Encrypt Data at Rest
                  </label>
                </div>
                <div className="flex items-center" data-id="element-1578">
                  <input type="checkbox" className="h-4 w-4 text-blue-600 rounded border-gray-300" defaultChecked data-id="element-1579" />
                  <label className="ml-2 text-sm text-gray-600" data-id="element-1580">
                    Key Rotation Enabled
                  </label>
                </div>
              </div>
            </div>
          </div>
          {/* Data Masking */}
          <div data-id="element-1581">
            <label className="block text-sm font-medium text-gray-700 mb-4" data-id="element-1582">
              Data Masking Rules
            </label>
            <div className="space-y-4" data-id="element-1583">
              {[{
              field: 'National ID',
              pattern: 'XXX-XX-1234',
              applies: ['Display', 'Export']
            }, {
              field: 'Phone Number',
              pattern: '+251-XX-XXX-1234',
              applies: ['Display']
            }, {
              field: 'Bank Account',
              pattern: 'XXXX-XXXX-1234',
              applies: ['Display', 'Export', 'API']
            }].map(rule => <div key={rule.field} className="border border-gray-200 rounded-lg p-4" data-id="element-1584">
                  <div className="flex items-center justify-between mb-2" data-id="element-1585">
                    <h4 className="text-sm font-medium text-gray-900" data-id="element-1586">
                      {rule.field}
                    </h4>
                    <div className="flex items-center space-x-2" data-id="element-1587">
                      <button className="text-sm text-gray-600 hover:text-gray-900" data-id="element-1588">
                        Edit
                      </button>
                      <button className="text-sm text-red-600 hover:text-red-700" data-id="element-1589">
                        Remove
                      </button>
                    </div>
                  </div>
                  <p className="text-sm text-gray-600 mb-2" data-id="element-1590">
                    Pattern: {rule.pattern}
                  </p>
                  <div className="flex flex-wrap gap-2" data-id="element-1591">
                    {rule.applies.map(context => <span key={context} className="px-2 py-1 text-xs bg-gray-100 text-gray-600 rounded" data-id="element-1592">
                        {context}
                      </span>)}
                  </div>
                </div>)}
            </div>
          </div>
        </div>
      </div>
      {/* Audit & Monitoring */}
      <div className="pt-8" data-id="element-1593">
        <h3 className="text-base font-semibold text-gray-900 mb-6" data-id="element-1594">
          Audit & Monitoring
        </h3>
        <div className="space-y-6" data-id="element-1595">
          {/* Audit Logging */}
          <div data-id="element-1596">
            <label className="block text-sm font-medium text-gray-700 mb-4" data-id="element-1597">
              Audit Log Configuration
            </label>
            <div className="border border-gray-200 rounded-lg p-4" data-id="element-1598">
              <div className="space-y-4" data-id="element-1599">
                <div className="flex items-center justify-between" data-id="element-1600">
                  <span className="text-sm text-gray-600" data-id="element-1601">Log Level</span>
                  <select className="text-sm border-gray-300 rounded-md" data-id="element-1602">
                    <option data-id="element-1603">INFO</option>
                    <option data-id="element-1604">DEBUG</option>
                    <option data-id="element-1605">WARN</option>
                    <option data-id="element-1606">ERROR</option>
                  </select>
                </div>
                <div className="flex items-center justify-between" data-id="element-1607">
                  <span className="text-sm text-gray-600" data-id="element-1608">
                    Retention Period
                  </span>
                  <select className="text-sm border-gray-300 rounded-md" data-id="element-1609">
                    <option data-id="element-1610">90 days</option>
                    <option data-id="element-1611">180 days</option>
                    <option data-id="element-1612">1 year</option>
                  </select>
                </div>
                <div data-id="element-1613">
                  <h4 className="text-sm font-medium text-gray-900 mb-2" data-id="element-1614">
                    Events to Log
                  </h4>
                  <div className="space-y-2" data-id="element-1615">
                    {['User Authentication', 'Data Modifications', 'System Configuration', 'Security Events', 'API Access'].map(event => <div key={event} className="flex items-center" data-id="element-1616">
                        <input type="checkbox" className="h-4 w-4 text-blue-600 rounded border-gray-300" defaultChecked data-id="element-1617" />
                        <label className="ml-2 text-sm text-gray-600" data-id="element-1618">
                          {event}
                        </label>
                      </div>)}
                  </div>
                </div>
              </div>
            </div>
          </div>
          {/* CAPTCHA Settings */}
          <div data-id="element-1619">
            <label className="block text-sm font-medium text-gray-700 mb-4" data-id="element-1620">
              CAPTCHA Configuration
            </label>
            <div className="border border-gray-200 rounded-lg p-4" data-id="element-1621">
              <div className="space-y-4" data-id="element-1622">
                <div className="flex items-center justify-between" data-id="element-1623">
                  <div data-id="element-1624">
                    <h4 className="text-sm font-medium text-gray-900" data-id="element-1625">
                      Enable CAPTCHA
                    </h4>
                    <p className="text-xs text-gray-500" data-id="element-1626">
                      Protect forms from automated submissions
                    </p>
                  </div>
                  <div className="relative inline-block w-12 mr-2 align-middle select-none" data-id="element-1627">
                    <input type="checkbox" defaultChecked className="toggle-checkbox absolute block w-6 h-6 rounded-full bg-white border-4 appearance-none cursor-pointer" data-id="element-1628" />
                    <label className="toggle-label block overflow-hidden h-6 rounded-full bg-gray-300 cursor-pointer" data-id="element-1629"></label>
                  </div>
                </div>
                <div data-id="element-1630">
                  <h4 className="text-sm font-medium text-gray-900 mb-2" data-id="element-1631">
                    Apply To
                  </h4>
                  <div className="space-y-2" data-id="element-1632">
                    {['Login Form', 'Registration Form', 'Password Reset', 'Contact Forms'].map(form => <div key={form} className="flex items-center" data-id="element-1633">
                        <input type="checkbox" className="h-4 w-4 text-blue-600 rounded border-gray-300" defaultChecked data-id="element-1634" />
                        <label className="ml-2 text-sm text-gray-600" data-id="element-1635">
                          {form}
                        </label>
                      </div>)}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>;
};